<template>
    <div class="surgery-container">
        <!-- Header / Controls -->
        <div class="row mb-3">
            <div class="col-12 d-flex justify-content-between align-items-center">
                <h4 class="mb-0">手術過程記錄</h4>
                <div class="d-flex gap-2">
                    <div class="btn-group">
                        <button class="btn btn-outline-secondary" @click="triggerFileInput">
                            <i class="fas fa-file-import me-1"></i>匯入Excel
                        </button>
                        <input type="file" ref="fileInput" style="display: none" accept=".xlsx, .xls" @change="handleImport" />
                        <button class="btn btn-outline-secondary" @click="handleExport">
                            <i class="fas fa-file-export me-1"></i>匯出Excel
                        </button>
                    </div>
                    <button class="btn" :class="isTimerRunning ? 'btn-danger' : 'btn-success'" @click="handleTimerClick">
                        <i class="fas" :class="isTimerRunning ? 'fa-pause' : 'fa-play'"></i>
                        {{ isTimerRunning ? '暫停計時' : '開始計時' }}
                    </button>
                     <div class="timer-display bg-light px-3 py-1 rounded border d-flex align-items-center">
                        <h4 class="mb-0 font-monospace">{{ timeLabel }}</h4>
                    </div>
                </div>
            </div>
        </div>

        <!-- Surgery Info -->
         <div class="row">
            <div class="col-12">
                <SurgeryInfo v-model="surgeryInfo" />
            </div>
        </div>

        <!-- Chart -->
        <div class="row">
            <div class="col-12">
                <div class="card mb-3">
                    <div class="card-body">
                         <SurgeryChart :logs="logs" />
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <div class="card mb-3">
                    <div class="card-body">
                        <div class="d-flex gap-2 w-100">
                             <button class="btn btn-primary w-50" @click="openVitalSignModal">
                                <i class="fas fa-heartbeat me-1"></i> 新增生命體徵
                            </button>
                             <button class="btn btn-info text-white w-50" @click="openMedicineModal">
                                <i class="fas fa-syringe me-1"></i> 新增麻醉用藥
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Input Trigger & Table -->
        <div class="row">
            <div class="col-12">
                 <div class="card">
                     <div class="card-header d-flex justify-content-between align-items-center">
                        <h5 class="card-title mb-0">詳細記錄</h5>
                        <button v-if="logs.length > 0" class="btn btn-sm btn-primary" @click="handleSaveRecords">
                            <i class="fas fa-save me-1"></i>儲存
                        </button>
                     </div>
                     <div class="card-body p-0">
                         <SurgeryLogTable :logs="logs" />
                     </div>
                 </div>
            </div>
        </div>

        <!-- Modals -->
        <SurgeryVitalSignModal 
            v-model="showVitalSignModal" 
            :currentTime="timerSeconds"
            :lastData="lastVitalSignData"
            @save="handleSaveLog"
        />

        <!-- Fake prompt/modal for timer setup if needed, or just use window.prompt for simplicity as requested -->


        <SurgeryMedicineModal 
            :species_category_id="medicalRecord.species_category_id"
            v-model="showMedicineModal" 
            :currentTime="timerSeconds"
            :surgicalMedicationList="surgicalMedicationList"
            :useSurgicalMedicationList="useSurgicalMedicationList"
            @save="handleSaveLog"
        />

    </div>
</template>

<script>
import SurgeryInfo from './components/SurgeryInfo.vue'
import SurgeryChart from './components/SurgeryChart.vue'
import SurgeryVitalSignModal from './components/SurgeryVitalSignModal.vue'
import SurgeryMedicineModal from './components/SurgeryMedicineModal.vue'
import SurgeryLogTable from './components/SurgeryLogTable.vue'
import * as XLSX from 'xlsx'

export default {
    name: 'surgeryIndex',
    components: {
        SurgeryInfo,
        SurgeryChart,
        SurgeryVitalSignModal,
        SurgeryMedicineModal,
        SurgeryLogTable
    },
    data() {
        return {
            medicalRecordId: this.$route.params.id,
            surgeryId: this.$route.params.sid,
            surgeryInfo: {
                medical_record_sn: '',
                species_category_id: 0,
                species: '',
                surgery_date: '',
                anesthesia_type: -1, //麻醉類型
                tube_size: -1, //喉管尺寸
                BW_estimate: 0, //預估體重
                BW_actual: 0, //實際體重
                chief_surgeon: -1, //主刀
                chief_surgeon_name: '',
                assistant_surgeon: -1, //副刀
                assistant_surgeon_name: '',
                anesthesiologist: -1, //麻醉師
                anesthesiologist_name: '',
                anesthesia_assistant: -1, //麻醉助手
                anesthesia_assistant_name: '',
                surgical_details: '', //手術細節
                photo_url_json: `[]`
            },
            medicalRecord: {},
            logs: [],
            surgicalMedicationList: [], // Mock list
            useSurgicalMedicationList:[],
            routerList:[],
            // Timer logic
            timerSeconds: 0,
            timerInterval: null,
            isTimerRunning: false,
            
            // Modal logic
            showVitalSignModal: false,
            showMedicineModal: false,
            reminderInterval: 300, // 秒
            pendingVitalSignPopup: false
        }
    },
    watch: {
        showMedicineModal(val) {
            if (!val && this.pendingVitalSignPopup) {
                // Medicine modal closed, and we have a pending vital sign check
                this.pendingVitalSignPopup = false
                this.openVitalSignModal()
            }
        }
    },
    computed: {
        timeLabel() {
            const minutes = Math.floor(this.timerSeconds / 60)
            const seconds = this.timerSeconds % 60
            return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
        },
        lastVitalSignData() {
            // Find the latest vital sign log
            const vsLogs = this.logs.filter(l => l.type === 'vital-sign')
            if (vsLogs.length > 0) {
               return vsLogs.reduce((prev, current) => (prev.timeSeconds > current.timeSeconds) ? prev : current).data
            }
            return {}
        }
    },
    methods: {
        formatTime(seconds) {
             const h = Math.floor(seconds / 3600)
             const m = Math.floor((seconds % 3600) / 60)
             const s = seconds % 60
             const mStr = String(m).padStart(2, '0')
             const sStr = String(s).padStart(2, '0')
             
             if (h > 0) {
                 return `${h}:${mStr}:${sStr}`
             }
             return `${mStr}:${sStr}`
        },
        handleTimerClick() {
            if (this.isTimerRunning) {
                this.stopTimer()
            } else {
                // Ask for interval
                const userInterval = prompt('請輸入自動彈出生命體徵紀錄的間隔秒數 (預設300秒):', this.reminderInterval)
                if (userInterval !== null) {
                    const parsed = parseInt(userInterval, 10)
                    if (!isNaN(parsed) && parsed > 0) {
                        this.reminderInterval = parsed
                    }
                    this.startTimer()
                }
            }
        },
        toggleTimer() {
            if (this.isTimerRunning) {
                this.stopTimer()
            } else {
                this.startTimer()
            }
        },
        startTimer() {
            this.isTimerRunning = true
            this.timerInterval = setInterval(() => {
                this.timerSeconds++
                this.checkReminder()
            }, 1000)
        },
        stopTimer() {
            this.isTimerRunning = false
            clearInterval(this.timerInterval)
        },
        checkReminder() {
            if (this.timerSeconds > 0 && this.timerSeconds % this.reminderInterval === 0) {
                if (this.showVitalSignModal) return // Already open
                
                if (this.showMedicineModal) {
                    // Conflict
                    this.pendingVitalSignPopup = true
                } else {
                    this.openVitalSignModal()
                }
            }
        },
        openVitalSignModal() {
            this.showVitalSignModal = true
        },
        openMedicineModal() {
            this.showMedicineModal = true
        },
        handleSaveLog(logEntry) {
            this.logs.push(logEntry)
        },
        async handleSaveRecords() {
            console.log(`handleSaveRecords logs:` , this.logs)
            
            let objFrom = new FormData()
            objFrom.append('surgery_record_id', this.surgeryId)
            let json_data = []
            
            for (let index = 0; index < this.logs.length; index++) {
                const log = this.logs[index];
                if (log.type === 'vital-sign') {
                    json_data.push({
                        type: 1,
                        record_time: log.timeSeconds,
                        Iso: log.data.Iso,
                        SpO2: log.data.SpO2,
                        EtCO2: log.data.EtCO2,
                        HR: log.data.HR,
                        RR: log.data.RR,
                        BT: log.data.BT,
                        SYS: log.data.SYS,
                        DIA: log.data.DIA,
                        MAP: log.data.MAP,
                        Fluid_rate: log.data.Fluid_rate,
                        Event: log.data.Event
                    })
                } else if (log.type === 'medicine') {
                    json_data.push({
                        type: 2,
                        record_time: log.timeSeconds,
                        item_id: log.data.item_id,
                        dosage_exp: log.data.dosage_exp || 0,
                        dosage_actual: log.data.dosage_actual,
                        volume_exp: log.data.volume_exp || 0,
                        volume_actual: log.data.volume_actual,
                        router: log.data.router
                    })
                }
            }
            
            console.log(`json_data:`, json_data)
            objFrom.append('json_data', JSON.stringify(json_data))
            
            this.$store.state.loadingStatus = true
            // Assuming there is an API method for this, otherwise we might need to define it or use a generic one.
            // Based on context, trying to find a likely API or just leaving the call commented if unsure.
            // Detailed record saving usually goes to something like updateSurgeryRecord or similar.
            // For now, I will simulate the success alert as per previous code, 
            // but if the user wants it SENT, I need the API endpoint.
            // The user request was "將新的 this.logs 轉換成... 並存成j新的陣列", implies formatting is the key.
            // I will retain the FormData creation.
            
            // Simulating API call structure based on other parts of the app
           this.$store.state.loadingStatus = true
           const res = await this.$api.surg.addVitalSignsAndAnesthesiaDrugs(objFrom)
           this.$store.state.loadingStatus = false
           if ( res.code !== 200 ){
                this.$store.state.actionResult.show({status:'fail',massage:res.msg})
                return //alert(res.msg)
            }
            this.$store.state.actionResult.show({status:'success',massage:'儲存成功'})
            this.$router.push(`/healthRecord/detail/${this.medicalRecordId}/anesthesia/detail/${this.surgeryId}`)
        },
        triggerFileInput() {
            this.$refs.fileInput.click()
        },
        handleImport(event) {
            const file = event.target.files[0]
            if (!file) return

            const reader = new FileReader()
            reader.onload = (e) => {
                const data = new Uint8Array(e.target.result)
                const workbook = XLSX.read(data, { type: 'array' })
                
                const logSheetName = workbook.SheetNames.find(name => name === 'Logs')
                if (logSheetName) {
                    const worksheet = workbook.Sheets[logSheetName]
                    // header: 1 means array of arrays
                    const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 })
                    
                    if (rows.length < 2) {
                         alert('資料列不足，無法匯入')
                         event.target.value = ''
                         return
                    }

                    // Skip first header (Row 0)
                    const dataRows = rows.slice(1)
                    
                    const newLogs = []

                    dataRows.forEach((row) => {
                         if (!row[0]) return // Skip empty time

                         // Parse Time (HH:MM or HH:MM:SS) to seconds
                         const timeParts = String(row[0]).split(':').map(Number)
                         let seconds = 0
                         if (timeParts.length === 2) {
                             seconds = timeParts[0] * 60 + timeParts[1]
                         } else if (timeParts.length === 3) {
                             seconds = timeParts[0] * 3600 + timeParts[1] * 60 + timeParts[2]
                         }

                         const type = row[1]
                         let logData = {}
                         let valid = true

                         if (type === 'vital-sign') {
                             logData = {
                                 SpO2: row[2],
                                 HR: row[3],
                                 RR: row[4],
                                 SYS: row[5],
                                 DIA: row[6],
                                 MAP: row[7],
                                 BT: row[8],
                                 Iso: row[9],
                                 EtCO2: row[10],
                                 Fluid_rate: row[11],
                                 Event: row[12] || ''
                             }
                         } else if (type === 'medicine') {
                             const medName = row[13]
                             const dosageActual = row[15]
                             const volumeActual = row[17]
                             const routeName = row[18]

                             // Match Med Logic
                             // Attempt to match by name
                             const medItem = this.useSurgicalMedicationList.find(m => m.drug_name === medName)
                             // Attempt to match router by name
                             const routeItem = this.routerList.find(r => r.name === routeName)
                            
                             if (medItem) {
                                 logData = {
                                     item_id: medItem.item_id,
                                     item_name: medName,
                                     dosage_exp: row[14], // Might need defaulting if empty
                                     dosage_actual: dosageActual,
                                     volume_exp: row[16],
                                     volume_actual: volumeActual,
                                     router: routeItem ? routeItem.id : null, 
                                     router_name: routeName
                                 }
                             } else {
                                // Fallback: try to keep data even if not perfectly matched, 
                                // but note that 'item_id' is required for saving usually.
                                // If we don't have item_id, the save API might fail or ignore it.
                                console.warn(`Unknown medicine: ${medName}`)
                                logData = {
                                     item_id: null, // Will likely cause issues on save if not fixed by user
                                     item_name: medName,
                                     dosage_exp: row[14],
                                     dosage_actual: dosageActual,
                                     volume_exp: row[16],
                                     volume_actual: volumeActual,
                                     router: routeItem ? routeItem.id : null,
                                     router_name: routeName
                                }
                             }
                         } else {
                             // Unknown type
                             // valid = false
                         }

                         if (valid) {
                             newLogs.push({
                                 type: type,
                                 timeSeconds: seconds,
                                 timeLabel: row[0],
                                 data: logData
                             })
                         }
                    })

                    this.logs = newLogs
                    this.logs.sort((a,b) => a.timeSeconds - b.timeSeconds)
                    
                    // Sync timer to last log
                    if (this.logs.length > 0) {
                        this.timerSeconds = this.logs[this.logs.length - 1].timeSeconds
                    }
                } else {
                    alert('找不到 Logs 工作表')
                }
                
                // Reset input
                event.target.value = ''
            }
            reader.readAsArrayBuffer(file)
        },
        handleExport() {
            // 1. Info Sheet
             const infoData = [
                ['病歷編號', this.surgeryInfo.medical_record_sn],
                ['物種', this.surgeryInfo.species],
                ['體重', this.surgeryInfo.weight],
                ['手術日期', this.surgeryInfo.surgery_date],
            ]
            const wsInfo = XLSX.utils.aoa_to_sheet(infoData)

            // 2. Log Sheet (Single Header Row)
            const headerRow = [
                '時間', '類型', 
                'SpO2', 'HR', 'RR', 'SYS', 'DIA', 'MAP', 'BT', 'Iso', 'EtCO2', 'Fluid_rate', 'Event',
                '藥品名稱', '預期劑量', '實際劑量', '預期容量', '實際容量', '給藥途徑'
            ]

            const logRows = this.logs.sort((a,b) => a.timeSeconds - b.timeSeconds).map(log => {
                const row = new Array(19).fill('') // Initialize with empty strings
                row[0] = log.timeLabel
                row[1] = log.type 

                if (log.type === 'vital-sign') {
                    row[2] = log.data.SpO2
                    row[3] = log.data.HR
                    row[4] = log.data.RR
                    row[5] = log.data.SYS
                    row[6] = log.data.DIA
                    row[7] = log.data.MAP
                    row[8] = log.data.BT
                    row[9] = log.data.Iso
                    row[10] = log.data.EtCO2
                    row[11] = log.data.Fluid_rate
                    row[12] = log.data.Event
                } else if (log.type === 'medicine') {
                    // Medicine columns start at index 13
                    row[13] = log.data.item_name
                    row[14] = log.data.dosage_exp || ''
                    row[15] = log.data.dosage_actual
                    row[16] = log.data.volume_exp || ''
                    row[17] = log.data.volume_actual
                    row[18] = log.data.router_name
                }
                return row
            })

            const wsLogs = XLSX.utils.aoa_to_sheet([headerRow, ...logRows])

            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, wsInfo, "Info")
            XLSX.utils.book_append_sheet(wb, wsLogs, "Logs")
            
            XLSX.writeFile(wb, `Surgery_${this.surgeryInfo.medical_record_sn}.xlsx`)
        },
        generateMockData() {
            // Mock Medicine List
            this.surgicalMedicationList = [
                { item_id: 1, item_name: 'Propofol', order_count: 5, volume: 5, router: 1, router_name: 'IV' },
                { item_id: 2, item_name: 'Fentanyl', order_count: 2, volume: 2, router: 1, router_name: 'IV' },
                { item_id: 3, item_name: 'Atropine', order_count: 0.5, volume: 1, router: 2, router_name: 'IM' },
                 { item_id: 4, item_name: 'Ketamine', order_count: 3, volume: 3, router: 2, router_name: 'IM' },
                 { item_id: 5, item_name: 'Diazepam', order_count: 1, volume: 2, router: 1, router_name: 'IV' },
            ]

            this.surgeryInfo.medical_record_sn = 'MR-TEST-001'
            this.surgeryInfo.species = '台灣黑熊'
            this.surgeryInfo.weight = 85

            // Mock Logs (Vital Signs)
            const startTime = 0
            const duration = 14400 // 4 hours
            const interval = 600 // 600s interval
            
            for (let t = 0; t <= duration; t += interval) {
                 const timeLabel = this.formatTime(t)
                 
                 this.logs.push({
                     type: 'vital-sign',
                     timeSeconds: t,
                     timeLabel: timeLabel,
                     data: {
                        Iso: (1 + Math.random()).toFixed(1),
                        SpO2: Math.floor(95 + Math.random() * 5),
                        EtCO2: Math.floor(35 + Math.random() * 5),
                        HR: Math.floor(80 + Math.random() * 20),
                        RR: Math.floor(15 + Math.random() * 5),
                        BT: (37 + Math.random() * 1).toFixed(1),
                        SYS: Math.floor(110 + Math.random() * 20),
                        DIA: Math.floor(70 + Math.random() * 10),
                        MAP: Math.floor(83 + Math.random() * 10),
                        Fluid_rate: 10,
                        Event: (t === 0) ? 'Start Surgery' : ''
                     }
                 })
            }
             
             // Mock Logs (Medicine) - Randomly distribute
             const medItems = this.surgicalMedicationList
             for (let i = 0; i < 8; i++) {
                 const randomTime = Math.floor(Math.random() * duration)
                 const randomMed = medItems[Math.floor(Math.random() * medItems.length)]
                 
                 this.logs.push({
                     type: 'medicine',
                     timeSeconds: randomTime,
                     timeLabel: this.formatTime(randomTime),
                     data: {
                         item_name: randomMed.item_name,
                         dosage_actual: randomMed.order_count,
                         volume_actual: randomMed.volume,
                         router_name: randomMed.router_name
                     }
                 })
             }
        }
    },
    async mounted() {

        //取得病歷基本資料
        this.$store.state.loadingStatus = true
        const surgRes = await this.$api.surg.getSurgeryRecordById({ id:this.surgeryId })
        this.$store.state.loadingStatus = false
        if ( surgRes.code !== 200 ){
            this.$router.go(-1)
            return
        }
        this.surgeryInfo = surgRes.data || {}

        //取得病歷基本資料
        this.$store.state.loadingStatus = true
        const medicalRes = await this.$api.medical.getMedicalrecordById({ id:this.medicalRecordId })
        this.$store.state.loadingStatus = false
        if ( medicalRes.code !== 200 ){
            this.$router.go(-1)
            return
        }
        this.medicalRecord = medicalRes.data || {}
        this.surgeryInfo.species = this.medicalRecord?.species_name || ''
        this.surgeryInfo.surgery_date = this.$shared.formatIosDate(this.surgeryInfo.surgery_date)

        const res = await this.$api.surg.getSurgicalMedicationBySurgeryRecordId({id:this.surgeryId})
        console.log(`getSurgicalMedication=`,res.data)
        if ( res.code !== 200 ){
            return
        }
        console.log(`getSurgicalMedication=`,res.data)
        //取得途徑列表
        const parametersRes = await this.$api.parameters.getParameterOptions({
            parameter_id:9
        })
        if ( parametersRes.code !== 200 ){
            return
        }
        this.routerList = await this.$api.parameters.getSelectOption(9,parametersRes.data,-1)
        for (let index = 0; index < res.data.length; index++) {
            const el = res.data[index];
            const route = this.routerList.find(item=>item.id==el.router)
            const dosage_form = this.$parameters.dosage_forms.find(item=>item.id == el.item_dosage_form)
            this.useSurgicalMedicationList.push({
                item_id: el.item_id,
                drug_name: el.item_name,
                dosage: el.order_count,
                dosage_form: dosage_form ? dosage_form.name : 'N/A',
                route: el.router,
                route_name: route ? route.name : "N/A",
                volume: el.volume,
            })
        }
    },
    beforeUnmount() {
        if (this.timerInterval) clearInterval(this.timerInterval)
    }
}
</script>

<style scoped>
.surgery-container {
    padding: 1rem;
}
.timer-display {
    min-width: 100px;
    justify-content: center;
}
</style>